import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { PageTitle } from "@/components/PageTitle";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { User, FileText, Activity, Search, Sparkles, MessageSquare, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { buildDeterministicAnswer } from "@/lib/patientLabSearch";
import { getFriendlyErrorMessage } from "@/lib/errors";

interface SearchResult {
  id: string;
  type: "patient" | "document" | "record" | "lab";
  title: string;
  subtitle?: string;
  description?: string;
  url: string;
  patientName?: string;
  date?: string;
}

export default function SearchResults() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const basePath = location.pathname.startsWith("/paciente") ? "/paciente" : "/app";
  const isPatient = basePath === "/paciente";
  const query = searchParams.get("q") || "";
  const [searchQuery, setSearchQuery] = useState(query);
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [aiAnswer, setAiAnswer] = useState<string | null>(null);

  useEffect(() => {
    if (query.length >= 2) {
      if (isPatient) performPatientAISearch(query);
      else performSearch(query);
    } else {
      setResults([]);
      setAiAnswer(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const performPatientAISearch = async (q: string) => {
    setLoading(true);
    setAiAnswer(null);
    setResults([]);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // 1) IA: resposta em linguagem natural (atalho determinístico + edge function)
      const aiPromise = (async () => {
        try {
          const direct = await buildDeterministicAnswer({ userId: user.id, query: q });
          if (direct.handled) return direct.answer;
          const { data, error } = await supabase.functions.invoke("ai-intelligent-search", {
            body: { query: q, scope: "all", userId: user.id, accessMode: "patient" },
          });
          if (error) throw error;
          if (data && !data.success && data.error) throw new Error(data.error);
          return (data?.answer || data?.data?.answer || null) as string | null;
        } catch (err) {
          console.error("[busca paciente] IA:", err);
          toast.error(getFriendlyErrorMessage(err));
          return null;
        }
      })();

      // 2) Referências cruas no banco (filtradas pelos prontuários do paciente)
      const refsPromise = (async () => {
        const { data: onboarding } = await supabase
          .from("patients_onboarding")
          .select("id, full_name")
          .eq("owner_id", user.id);

        const ids = (onboarding || []).map((p) => p.id);
        if (ids.length === 0) return [] as SearchResult[];

        const term = `%${q.toLowerCase()}%`;
        const collected: SearchResult[] = [];

        const [{ data: labs }, { data: docs }] = await Promise.all([
          supabase
            .from("lab_results_onboarding")
            .select("id, test_name, value, value_text, unit, collected_at, patient_id")
            .in("patient_id", ids)
            .ilike("test_name", term)
            .order("collected_at", { ascending: false, nullsFirst: false })
            .limit(30),
          supabase
            .from("documents_onboarding")
            .select("id, file_name, ai_summary, created_at, patient_id")
            .in("patient_id", ids)
            .or(`file_name.ilike.${term},ai_summary.ilike.${term}`)
            .limit(20),
        ]);

        (labs || []).forEach((l) => {
          const valueDisplay =
            l.value != null ? `${l.value}${l.unit ? ` ${l.unit}` : ""}` : l.value_text || "—";
          collected.push({
            id: l.id,
            type: "lab",
            title: l.test_name,
            subtitle: valueDisplay,
            date: l.collected_at ? new Date(l.collected_at).toLocaleDateString("pt-BR") : undefined,
            url: `/paciente/minha-saude`,
          });
        });

        (docs || []).forEach((d) => {
          collected.push({
            id: d.id,
            type: "document",
            title: d.file_name,
            subtitle: d.ai_summary?.substring(0, 120) || "",
            date: new Date(d.created_at).toLocaleDateString("pt-BR"),
            url: `/paciente/relatorios`,
          });
        });

        return collected;
      })();

      const [answer, refs] = await Promise.all([aiPromise, refsPromise]);
      setAiAnswer(answer);
      setResults(refs);
    } catch (error) {
      console.error("Patient AI search error:", error);
      toast.error(getFriendlyErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const performSearch = async (searchQuery: string) => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const searchTerm = `%${searchQuery.toLowerCase()}%`;
      const searchResults: SearchResult[] = [];

      const { data: patients } = await supabase
        .from("patients_onboarding")
        .select("id, full_name, cpf, email")
        .eq("owner_id", user.id)
        .or(`full_name.ilike.${searchTerm},cpf.ilike.${searchTerm}`)
        .limit(20);

      if (patients) {
        patients.forEach((p) => {
          searchResults.push({
            id: p.id,
            type: "patient",
            title: p.full_name,
            subtitle: p.cpf || p.email || "",
            url: `${basePath}/pacientes/${p.id}`,
          });
        });
      }

      const { data: documents } = await supabase
        .from("documents_onboarding")
        .select(
          `id, file_name, created_at, ai_summary, patient_id, patients_onboarding!inner(full_name)`,
        )
        .eq("owner_id", user.id)
        .or(`file_name.ilike.${searchTerm},ai_summary.ilike.${searchTerm}`)
        .limit(20);

      if (documents) {
        documents.forEach((d: any) => {
          searchResults.push({
            id: d.id,
            type: "document",
            title: d.file_name,
            subtitle: d.ai_summary?.substring(0, 100) || "",
            patientName: d.patients_onboarding?.full_name,
            date: new Date(d.created_at).toLocaleDateString("pt-BR"),
            url: `${basePath}/documentos/${d.id}`,
          });
        });
      }

      setResults(searchResults);
    } catch (error) {
      console.error("Search error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim().length >= 2) {
      setSearchParams({ q: searchQuery });
    }
  };

  const getIcon = (type: SearchResult["type"]) => {
    switch (type) {
      case "patient":
        return <User className="h-5 w-5" />;
      case "document":
        return <FileText className="h-5 w-5" />;
      case "record":
      case "lab":
        return <Activity className="h-5 w-5" />;
    }
  };

  const groupedResults = {
    patient: results.filter((r) => r.type === "patient"),
    document: results.filter((r) => r.type === "document"),
    lab: results.filter((r) => r.type === "lab"),
    record: results.filter((r) => r.type === "record"),
  };

  const totalResults = results.length;
  const hasNoContent = !loading && totalResults === 0 && !aiAnswer && query.length >= 2;

  return (
    <div className="space-y-6">
      <div>
        <PageTitle>Pesquisa</PageTitle>
        <p className="text-muted-foreground mt-2">
          {query ? `Resultados para "${query}"` : "Digite para buscar"}
        </p>
      </div>

      <form onSubmit={handleSearch}>
        <div className="relative max-w-2xl">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
          <Input
            placeholder={
              isPatient
                ? "Pergunte sobre seus exames, documentos..."
                : "Buscar pacientes, documentos, registros..."
            }
            className="pl-10 pr-4 h-12 text-base"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            autoFocus
          />
        </div>
      </form>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : hasNoContent ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <Search className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">Nenhum resultado encontrado</h3>
            <p className="text-muted-foreground">
              Tente buscar com outros termos ou verifique a ortografia
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {isPatient && aiAnswer && (
            <Card className="border-primary/30">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-primary">
                  <Sparkles className="h-5 w-5" />
                  Resposta da IA
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm leading-relaxed whitespace-pre-wrap">{aiAnswer}</p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    navigate(`/paciente/chat-ia`, {
                      state: { initialQuery: query, initialAnswer: aiAnswer },
                    })
                  }
                >
                  <MessageSquare className="mr-2 h-4 w-4" />
                  Continuar no Chat IA
                </Button>
              </CardContent>
            </Card>
          )}

          {groupedResults.lab.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="h-5 w-5" />
                  Exames ({groupedResults.lab.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {groupedResults.lab.map((result) => (
                  <button
                    key={`${result.type}-${result.id}`}
                    onClick={() => navigate(result.url)}
                    className="w-full flex items-center gap-4 p-4 rounded-lg hover:bg-muted transition-colors text-left border"
                  >
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-green-500/10 flex items-center justify-center text-green-600">
                      {getIcon(result.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{result.title}</div>
                      <div className="text-sm text-muted-foreground">
                        {result.subtitle}
                        {result.date && ` • ${result.date}`}
                      </div>
                    </div>
                  </button>
                ))}
              </CardContent>
            </Card>
          )}

          {groupedResults.patient.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="h-5 w-5" />
                  Pacientes ({groupedResults.patient.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {groupedResults.patient.map((result) => (
                  <button
                    key={`${result.type}-${result.id}`}
                    onClick={() => navigate(result.url)}
                    className="w-full flex items-center gap-4 p-4 rounded-lg hover:bg-muted transition-colors text-left border"
                  >
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                      {getIcon(result.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{result.title}</div>
                      {result.subtitle && (
                        <div className="text-sm text-muted-foreground truncate">
                          {result.subtitle}
                        </div>
                      )}
                    </div>
                  </button>
                ))}
              </CardContent>
            </Card>
          )}

          {groupedResults.document.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Documentos ({groupedResults.document.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {groupedResults.document.map((result) => (
                  <button
                    key={`${result.type}-${result.id}`}
                    onClick={() => navigate(result.url)}
                    className="w-full flex items-center gap-4 p-4 rounded-lg hover:bg-muted transition-colors text-left border"
                  >
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center text-blue-600">
                      {getIcon(result.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{result.title}</div>
                      <div className="text-sm text-muted-foreground">
                        {result.patientName && `Paciente: ${result.patientName}`}
                        {result.date && ` • ${result.date}`}
                      </div>
                      {result.subtitle && (
                        <div className="text-xs text-muted-foreground truncate mt-1">
                          {result.subtitle}
                        </div>
                      )}
                    </div>
                  </button>
                ))}
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
